import React, { useState, useEffect } from 'react'
import { toast } from '../../lib/toast'
import {
  Shield, Save, AlertCircle,
  CheckCircle, Copy, FileText, Power,
} from 'lucide-react'
import JsonEditor from './../jsonEditor/JsonEditor'
import styles from './SSOConfiguration.module.css'
import { validateAndCleanInput } from '../../utils/security'
import { useProvidersStore } from '../../store/providersStore'
import { useAuthStore } from '../../store/authStore'
import { CreateIdpProviderDto } from '../../types/types'
import { UserRole } from '../../enums/userRole'
import { idpProvidersApi } from '../../lib/apiClient'
import InputModal from '../../ui/inputModal/InputModal'
import AzureProviderForm from './providers/AzureProviderForm'
import OktaProviderForm from './providers/OktaProviderForm'
import GoogleProviderForm from './providers/GoogleProviderForm'
import SamlProviderForm from './providers/SamlProviderForm'
import IasProviderForm from './providers/IasProviderForm'
import sapSvg from '../../assets/providers/sap.svg'
import oktaSvg from '../../assets/providers/okta.svg'
import azureSvg from '../../assets/providers/azure.svg'
import googleSvg from '../../assets/providers/google.svg'
import { hasMinRole } from '../../lib/permissions'

interface SSOProvider {
  id: string
  name: string
  description: string
  icon: React.ReactNode
}


const EMPTY_PROVIDER_FORM: CreateIdpProviderDto = {
  type: 'AZURE',
  name: '',
  description: '',
  issuerUrl: '',
  clientId: '',
  clientSecret: '',
  authorizationUrl: '',
  tokenUrl: '',
  config: {}
}

const ssoProviders: SSOProvider[] = [
  {
    id: 'okta',
    name: 'Okta',
    description: 'Okta identity provider integration',
    icon: (
      <img
        src={oktaSvg}
        alt="Okta"
        style={{ width: 24, height: 24 }}
      />
    )
  },
  // {
  //   id: 'saml',
  //   name: 'SAML 2.0',
  //   description: 'Enterprise SAML identity provider',
  //   icon: (
  //     <img
  //       src={samlSvg}
  //       alt="SAML"
  //       style={{ width: 24, height: 24 }}
  //     />
  //   )
  // },
  {
    id: 'azure_ad',
    name: 'Azure AD',
    description: 'Microsoft Azure Active Directory',
    icon: (
      <img
        src={azureSvg}
        alt="Azure AD"
        style={{ width: 24, height: 24 }}
      />
    )
  },
  {
    id: 'google',
    name: 'Google',
    description: 'Google Workspace SSO',
    icon: (
      <img
        src={googleSvg}
        alt="Google"
        style={{ width: 24, height: 24 }}
      />
    )
  },
  {
    id: 'ias',
    name: 'IAS',
    description: 'SAP Identity Authentication Service',
    icon: (
      <img
        src={sapSvg}
        alt="SAP IAS"
        style={{ height: 24 }}
      />
    )
  }
]

export default function SSOConfiguration() {
  const { providers, fetchProvidersAdmin, fetchProviders, createProvider, updateProvider, deleteProvider } = useProvidersStore()
  const currentUser = useAuthStore(state => state.user)

  // Provider management state
  const [providerForm, setProviderForm] = useState<CreateIdpProviderDto>({ ...EMPTY_PROVIDER_FORM })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [deletingProviderId, setDeletingProviderId] = useState<string | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null) // ID from providers store
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testResult, setTestResult] = useState<any>(null)
  const [showMetadata, setShowMetadata] = useState(false)
  const [metadata,] = useState('')


  useEffect(() => {
    if (!currentUser || !hasMinRole(currentUser?.role?.id, UserRole.Admin)) {
      toast.error('You do not have permission to access SSO configuration')
      return
    }
    else {
      fetchProvidersAdmin()
      setLoading(false)
    }
  }, [])

  // --- Provider CRUD handlers ---


  const handleConfirmDelete = async () => {
    if (!deleteTargetId || deletingProviderId === deleteTargetId) return
    try {
      setDeletingProviderId(deleteTargetId)
      await deleteProvider(deleteTargetId)
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete provider')
    } finally {
      setDeletingProviderId(null)
      setShowDeleteModal(false)
      setDeleteTargetId(null)
    }
  }

  // --- SSO config handlers (existing) ---

  const handleProviderSelect = async (providerId: string): Promise<void> => {
    // Map template provider ID to provider type
    const providerTypeMap: Record<string, string> = {
      'okta': 'OKTA',
      'azure_ad': 'AZURE',
      'google': 'GOOGLE',
      'saml': 'SAML', // SAML is separate from IAS
      'ias': 'IAS'
    }

    const providerType = providerTypeMap[providerId] || providerId.toUpperCase()

    // Find existing provider of this type, or create a new one
    let existingProvider = providers.find(p => p.type === providerType)

    // Check if provider is active
    if (existingProvider && (existingProvider as any).active === false) {
      toast.error('This provider is disabled and cannot be configured')
      return
    }

    setSelectedProvider(providerId)
    setTestResult(null)

    if (!existingProvider) {
      // Create a new provider in the store
      try {
        const newProviderData: CreateIdpProviderDto = {
          type: providerType,
          name: `${providerType} SSO Provider`,
          description: `SSO provider for ${providerId}`,
          issuerUrl: '',
          clientId: '',
          clientSecret: '',
          authorizationUrl: '',
          tokenUrl: '',
          config: {}
        }
        await createProvider(newProviderData)
        // Fetch providers again to get the new provider's ID
        await fetchProvidersAdmin()
        // Get the updated providers list
        const updatedProviders = useProvidersStore.getState().providers
        existingProvider = updatedProviders.find(p => p.type === providerType)
      } catch (error: any) {
        toast.error(`Failed to create provider: ${error.message}`)
        return
      }
    }

    if (existingProvider) {
      setSelectedProviderId(existingProvider.id)
      // Load provider data into the form for editing
      setProviderForm({
        type: existingProvider.type,
        name: existingProvider.name,
        description: existingProvider.description || '',
        issuerUrl: existingProvider.issuerUrl || '',
        clientId: existingProvider.clientId || '',
        clientSecret: existingProvider.clientSecret || '',
        authorizationUrl: existingProvider.authorizationUrl || '',
        tokenUrl: existingProvider.tokenUrl || '',
        config: existingProvider.config || {}
      })
    }
  }

  const handleProviderFormFieldChange = (field: keyof CreateIdpProviderDto, value: string) => {
    let sanitizedValue = value

    // Sanitize based on field type
    if (field === 'description') {
      sanitizedValue = validateAndCleanInput(value)
    } else if (field === 'issuerUrl' || field === 'authorizationUrl' || field === 'tokenUrl') {
      sanitizedValue = validateAndCleanInput(value)
    } else if (field === 'clientId' || field === 'clientSecret') {
      sanitizedValue = validateAndCleanInput(value)
    } else if (field === 'name') {
      sanitizedValue = validateAndCleanInput(value)
    }

    // Update provider form state only (don't save to store yet)
    setProviderForm(prev => ({
      ...prev,
      [field]: sanitizedValue
    }))
  }

  const handleConfigChange = (key: string, value: string) => {
    // Update provider form config state only (don't save to store yet)
    setProviderForm(prev => ({
      ...prev,
      config: {
        ...prev.config || {},
        [key]: value
      }
    }))
  }

  const handleToggleProviderActive = async (providerId: string, currentActive: boolean) => {
    if (!hasMinRole(currentUser?.role?.id, UserRole.Admin)) {
      toast.error('Only super admins can enable/disable providers')
      return
    }

    // Map template provider ID to provider type
    const providerTypeMap: Record<string, string> = {
      'okta': 'OKTA',
      'azure_ad': 'AZURE',
      'google': 'GOOGLE',
      'saml': 'SAML', // SAML is separate from IAS
      'ias': 'IAS'
    }
    const providerType = providerTypeMap[providerId] || providerId.toUpperCase()
    let existingProvider = providers.find(p => p.type === providerType)

    // If provider doesn't exist, create it first
    if (!existingProvider) {
      try {
        const providerNameMap: Record<string, string> = {
          'okta': 'Okta',
          'azure_ad': 'Azure AD',
          'google': 'Google',
          'saml': 'SAML 2.0',
          'ias': 'SAP IAS'
        }
        const providerName = providerNameMap[providerId] || providerId

        const newProviderData: CreateIdpProviderDto = {
          type: providerType,
          name: `${providerName} SSO Provider`,
          description: `SSO provider for ${providerName}`,
          issuerUrl: '',
          clientId: '',
          clientSecret: '',
          authorizationUrl: '',
          tokenUrl: '',
          config: {}
        }
        await createProvider(newProviderData)
        await fetchProvidersAdmin()
        // Get the updated providers list
        const updatedProviders = useProvidersStore.getState().providers
        existingProvider = updatedProviders.find(p => p.type === providerType)

        if (!existingProvider) {
          toast.error('Failed to create provider')
          return
        }
      } catch (error: any) {
        toast.error(`Failed to create provider: ${error.message}`)
        return
      }
    }

    try {
      const newActiveState = !currentActive
      await idpProvidersApi.toggleActive(existingProvider!.id, newActiveState)
      await Promise.all([fetchProvidersAdmin(), fetchProviders()])
      toast.success(newActiveState ? 'Provider enabled' : 'Provider disabled')
    } catch (error: any) {
      toast.error(`Failed to toggle provider: ${error.message}`)
    }
  }

  const handleSave = async () => {
    if (!selectedProvider || !selectedProviderId) {
      toast.error('Please select an SSO provider')
      return
    }

    if (!providerForm.name?.trim()) {
      toast.error('Provider name is required')
      return
    }

    setSaving(true)
    try {
      await updateProvider(selectedProviderId, {
        ...providerForm,
        // SSO provider secrets are submitted as plain text and stored without client-side encryption.
        clientSecret: providerForm.clientSecret ?? '',
      })
    } catch (error: any) {
      toast.error(error.message || 'Failed to save provider')
    } finally {
      setSaving(false)
    }
  }

  const generateMetadata = async () => {
    if (selectedProvider !== 'saml') {
      toast.error('Metadata generation is only available for SAML')
      return
    }

    try {
      //todo implement
    } catch (error: any) {
      toast.error(`Failed to generate metadata: ${error.message}`)
    }
  }

  const copyMetadata = () => {
    navigator.clipboard.writeText(metadata)
    toast.success('SAML metadata copied to clipboard!')
  }

  const selectedProviderFromStore = selectedProviderId
    ? providers.find(p => p.id === selectedProviderId)
    : null

  if (loading) {
    return <div className={styles.loading}>Loading SSO configuration...</div>
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <Shield size={24} />
          <div>
            <h2>Single Sign-On Configuration</h2>
            <p>Manage identity providers and configure SSO for your organization</p>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {/* ====== IDP Providers Section ====== */}


        {/* ====== SSO Template Configuration (existing) ====== */}
        <div className={styles.providerSelection}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Select SSO Provider</h3>

          </div>
          <div className={styles.providerGrid}>
            {ssoProviders.map((provider) => {
              // Map template provider ID to provider type
              const providerTypeMap: Record<string, string> = {
                'okta': 'OKTA',
                'azure_ad': 'AZURE',
                'google': 'GOOGLE',
                'saml': 'SAML', // SAML is separate from IAS
                'ias': 'IAS'
              }
              const providerType = providerTypeMap[provider.id] || provider.id.toUpperCase()
              const existingProvider = providers.find(p => p.type === providerType)
              // Provider is disabled if it doesn't exist OR if active is false
              const isActive = existingProvider ? (existingProvider as any).active !== false : false
              const isDisabled = !isActive

              return (
                <div
                  key={provider.id}
                  className={`${styles.providerCard} ${selectedProvider === provider.id ? styles.providerCardActive : ''
                    } ${isDisabled ? styles.providerCardDisabled : ''}`}
                  onClick={() => !isDisabled && handleProviderSelect(provider.id)}
                  style={{
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.5 : 1,
                    position: 'relative'
                  }}
                >
                  {hasMinRole(currentUser?.role?.id, UserRole.Admin) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        const currentActive = existingProvider ? (existingProvider as any).active !== false : false
                        handleToggleProviderActive(provider.id, currentActive)
                      }}
                      className={`${styles.toggleButton} ${isActive ? styles.toggleButtonActive : styles.toggleButtonInactive}`}
                      title={existingProvider
                        ? (isActive ? 'Disable provider' : 'Enable provider')
                        : 'Create and enable provider'}
                    >
                      <Power size={14} />
                    </button>
                  )}
                  <div className={styles.providerIcon}>
                    {provider.icon}
                  </div>
                  <h4>{provider.name}</h4>
                  <p>{provider.description}</p>
                </div>
              )
            })}
          </div>
        </div>

        {selectedProviderFromStore && selectedProvider && (selectedProviderFromStore as any).active !== false && (
          <div className={styles.configurationSection}>
            <div className={styles.sectionHeader}>
              <h3>
                {selectedProviderFromStore.name} Configuration
              </h3>
              <div className={styles.sectionActions}>
                {selectedProvider === 'saml' && (
                  <button
                    onClick={generateMetadata}
                    className={styles.metadataBtn}
                  >
                    <FileText size={16} />
                    Generate Metadata
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={styles.saveBtn}
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>

            <div className={styles.configForm}>
              {selectedProvider === 'azure_ad' && (
                <AzureProviderForm
                  providerForm={providerForm}
                  onFieldChange={handleProviderFormFieldChange}
                  onConfigChange={handleConfigChange}
                  hasClientSecret={selectedProviderFromStore?.hasClientSecret}
                />
              )}
              {selectedProvider === 'okta' && (
                <OktaProviderForm
                  providerForm={providerForm}
                  onFieldChange={handleProviderFormFieldChange}
                  onConfigChange={handleConfigChange}
                  hasClientSecret={selectedProviderFromStore?.hasClientSecret}
                />
              )}
              {selectedProvider === 'google' && (
                <GoogleProviderForm
                  providerForm={providerForm}
                  onFieldChange={handleProviderFormFieldChange}
                  onConfigChange={handleConfigChange}
                  hasClientSecret={selectedProviderFromStore?.hasClientSecret}
                />
              )}
              {selectedProvider === 'saml' && (
                <SamlProviderForm
                  providerForm={providerForm}
                  onFieldChange={handleProviderFormFieldChange}
                  onConfigChange={handleConfigChange}
                />
              )}
              {selectedProvider === 'ias' && (
                <IasProviderForm
                  providerForm={providerForm}
                  onFieldChange={handleProviderFormFieldChange}
                  onConfigChange={handleConfigChange}
                  hasClientSecret={selectedProviderFromStore?.hasClientSecret}
                />
              )}
            </div>

            {testResult && (
              <div className={styles.testResult}>
                <h4>Configuration Test Result</h4>
                <div className={`${styles.resultCard} ${testResult.valid ? styles.success : styles.error}`}>
                  <div className={styles.resultIcon}>
                    {testResult.valid ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                  </div>
                  <div className={styles.resultContent}>
                    <div className={styles.resultStatus}>
                      {testResult.valid ? 'Configuration Valid' : 'Configuration Error'}
                    </div>
                    <div className={styles.resultMessage}>
                      {testResult.message}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {showMetadata && metadata && (
          <div className={styles.metadataModal}>
            <div className={styles.modalOverlay} onClick={() => setShowMetadata(false)} />
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h3>SAML Service Provider Metadata</h3>
                <div className={styles.modalActions}>
                  <button onClick={copyMetadata} className={styles.copyBtn}>
                    <Copy size={16} />
                    Copy
                  </button>
                  <button onClick={() => setShowMetadata(false)} className={styles.closeBtn}>
                    Close
                  </button>
                </div>
              </div>
              <div className={styles.modalBody}>
                <JsonEditor
                  value={metadata}
                  onChange={() => { }}
                  readOnly={true}
                  language="xml"
                  height="400px"
                />
              </div>
            </div>
          </div>
        )}

      </div>

      <InputModal
        open={showDeleteModal}
        title="Delete Provider"
        message="Are you sure you want to delete this provider? This action cannot be undone."
        showInput={false}
        confirmText="Delete"
        cancelText="Cancel"
        loading={showDeleteModal && deleteTargetId !== null && deletingProviderId === deleteTargetId}
        loadingText="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deletingProviderId) {
            setShowDeleteModal(false);
            setDeleteTargetId(null);
          }
        }}
      />
    </div>
  )
}
